import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import { batchService } from "/app/src/services";
import { PowerPickTimeZoneToLocal } from "/app/src/components/generic/formatting/dateTime";
import Search from "../search";
import { useTranslation } from "react-i18next";
import { Batch as BatchType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
export default function Batches() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<BatchType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: t("translation:name"),
      }),
      columnHelper.accessor("priority", {
        id: "priority",
        cell: (info) => info.getValue(),
        header: t("translation:priority"),
      }),
      columnHelper.accessor("creationDate", {
        id: "creationDate",
        cell: (info) => {
          return (
            <PowerPickTimeZoneToLocal
              date={info.getValue()}
              format={"YYYY-MM-DD HH:mm:ss"}
            />
          );
        },
        header: t("translation:creation_date"),
      }),
    ],
    [columnHelper, t],
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState("");

  const goToBatch = useCallback(
    (batchId: number) => {
      navigate(`/explorer/batches/${batchId}`);
    },
    [navigate],
  );

  const {
    data: batches,
    count: batchesCount,
    isFetching,
  } = usePaginatedData<BatchType>({
    queryKey: ["batches"],
    searchString,
    page,
    pageSize,
    sort,
    service: batchService,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:batches")} - ItemPath</title>
      </Helmet>
      <div className="search box">
        <Search setSearchString={setSearchString} />
      </div>
      <Table
        loading={isFetching}
        rows={batches}
        tableColumns={columns}
        length={batchesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage,
          setPageSize,
        }}
        enableRowSelection
        rowClicked={goToBatch}
        emptyText={t("translation:no_batches")}
      />
    </div>
  );
}
